import type { Notification } from "@/hooks/useNotifications";

/** Convert ISO/date string to local YYYY-MM-DD (avoids UTC midnight off-by-one). */
function toLocalDateParam(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Resolves the deep-link URL for a notification.
 * Uses `action_url` if present, otherwise derives from entity_type / entity_id / project_id.
 */
export function resolveNotificationUrl(n: Notification): string | null {
  if (n.action_url) return n.action_url;

  const id = n.entity_id;
  const pid = n.project_id;

  switch (n.entity_type) {
    // --- Projects ---
    case "project":
      return `/controller/projects/${id}`;

    // --- Bills ---
    case "bill":
      return pid
        ? `/controller/projects/${pid}?tab=work_orders`
        : `/controller/bills`;

    // --- Invoices ---
    case "invoice":
      return pid
        ? `/controller/projects/${pid}?tab=invoices`
        : `/controller/invoices`;

    // --- Work Orders ---
    case "work_order":
      return pid
        ? `/controller/projects/${pid}?tab=work_orders`
        : `/controller/projects`;

    // --- Approvals ---
    case "approval_request":
    case "approval":
      return `/controller/approvals`;

    // --- Timesheets / Personnel ---
    case "timesheet":
      return pid
        ? `/controller/personnel/${pid}?tab=timesheets`
        : `/controller/personnel`;

    // --- Workers ---
    case "worker":
      return pid
        ? `/controller/personnel/${pid}?tab=workers`
        : `/controller/personnel`;

    // --- Estimates ---
    case "estimate":
      return `/controller/estimates/${id}`;

    // --- Customers ---
    case "customer":
      return `/controller/customers/${id}`;

    // --- Vendors ---
    case "vendor":
      return `/controller/vendors/${id}`;

    // --- Calendar events ---
    case "calendar_event": {
      const params = new URLSearchParams();
      const dStr = toLocalDateParam(n.due_date);
      if (dStr) params.set("date", dStr);
      params.set("view", "day");
      if (id) params.set("eventId", id);
      const qs = params.toString();
      return qs ? `/controller/calendar?${qs}` : `/controller/calendar`;
    }

    // --- Change orders ---
    case "change_order":
      return pid
        ? `/controller/projects/${pid}?tab=change_orders`
        : `/controller/projects`;

    // --- Conversations / messages ---
    case "conversation":
    case "message":
      return `/controller/comms`;

    // --- Compliance ---
    case "compliance":
      return `/controller/compliance`;

    // --- Support tickets ---
    case "support_ticket":
      return `/controller/support`;

    default:
      return null;
  }
}
